import { useState, useEffect } from "react";

import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { authenticationAtom } from "../State";
import { userAPI } from "../UserAPI";
import ToolConfigurationForm from "../real-gain/ToolConfigurationForm";
import type { ConfigurationParameter } from "../real-gain/ConfigurationSchemaEditor";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
interface ToolConfigurationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  offerId: string;
  productName: string;
  configurationSchema?: ConfigurationParameter[];
}

export default function ToolConfigurationDialog({
  isOpen,
  onClose,
  offerId,
  productName,
  configurationSchema: schemaFromProps,
}: ToolConfigurationDialogProps) {
  const { t } = useTranslation();
  const [authentication] = useAtom(authenticationAtom) as any;
  const [schema, setSchema] = useState<ConfigurationParameter[]>(schemaFromProps ?? []);
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !offerId || !authentication?.customer) {
      setSchema([]);
      setValues({});
      return;
    }
    const load = async () => {
      setLoading(true);
      try {
        let configSchema = schemaFromProps ?? [];
        if (configSchema.length === 0) {
          const offer = await userAPI.getMarketplaceOfferById(offerId);
          configSchema = offer?.configurationSchema ?? [];
        }
        setSchema(configSchema);

        const config = await userAPI.getToolConfiguration(authentication.customer, offerId);
        setValues(config?.schemaConfiguration ?? {});
      } catch {
        setValues({});
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen, offerId, authentication?.customer]);

  const handleSave = async () => {
    if (!offerId || !authentication?.customer) return;
    setSaving(true);
    try {
      const strValues: Record<string, string> = {};
      for (const [k, v] of Object.entries(values)) {
        strValues[k] = String(v ?? "");
      }
      await userAPI.putToolConfiguration(
        authentication.customer,
        offerId,
        strValues,
        schema,
      );
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{t("ui.toolConfigurationValues")}</DialogTitle>
          <DialogDescription>{productName}</DialogDescription>
        </DialogHeader>
        {loading ? (
          <p className="text-sm text-muted-foreground">{t("global.loading")}</p>
        ) : schema.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("ui.noConfigurationSchema")}</p>
        ) : (
          <ToolConfigurationForm schema={schema} value={values} onChange={setValues} />
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>{t("global.close")}</Button>
          <Button type="button" onClick={handleSave} disabled={loading || schema.length === 0 || saving}>{t("global.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
